// Implements: F-UIX-020, F-UIX-003
/**
 * WCAG 2.x contrast, the mirror of `backend/.../design/domain/contrast.py`.
 *
 * The editor shows a pair's ratio as the colours change, without a round trip,
 * so it needs the server's formula and not an approximation of it: both sides
 * read the same golden vectors (`docs/architecture/fixtures/contrast-vectors.json`)
 * in their tests. The ratio is compared **unrounded** against 4.5, because
 * rounding first would let 4.497 pass here and fail on the server. The server
 * stays authoritative: this only decides what the interface shows and when the
 * save button is enabled.
 */

/** WCAG AA for body text; exactly 4.5 passes. */
export const AA_MINIMUM_RATIO = 4.5;

export type Channels = readonly [number, number, number];

const RED = 0.2126;
const GREEN = 0.7152;
const BLUE = 0.0722;

/**
 * `#rgb` or `#rrggbb`, any case, and nothing else: exactly the server's `fullmatch`
 * (`hex_color.py`). Nothing is trimmed, because the server trims nothing: a blank
 * around a colour is refused there, so here it is "not a colour" and blocks the save.
 * `$` without the `m` flag matches only at the very end, so a trailing newline fails too.
 */
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** The three 0..255 channels of a colour, or `null` when it is not one. */
export function parseHexColor(value: string): Channels | null {
  const match = HEX_COLOR.exec(value);
  const digits = match?.[1];
  if (digits === undefined) return null;
  const full =
    digits.length === 3
      ? digits
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : digits;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

function linear(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([red, green, blue]: Channels): number {
  return RED * linear(red) + GREEN * linear(green) + BLUE * linear(blue);
}

/**
 * The unrounded ratio, 1 to 21, symmetric in its arguments; `null` when either
 * value is not a colour, so a half-typed value is "unknown", never "passes".
 */
export function contrastRatio(first: string, second: string): number | null {
  const a = parseHexColor(first);
  const b = parseHexColor(second);
  if (a === null || b === null) return null;
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (left, right) => right - left,
  ) as [number, number];
  return (lighter + 0.05) / (darker + 0.05);
}

/** True when the ratio reaches 4.5:1 without rounding. */
export function meetsAA(ratio: number): boolean {
  return ratio >= AA_MINIMUM_RATIO;
}
